import { collection } from '@/infra/collection'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import { Subject } from 'rxjs'
import type { Section7216Api } from '@/features/account/api/section-7216-api'
import type {
  Section7216AcceptanceResource,
  Section7216Resource,
} from '@/features/account/api/resources/section-7216'
import { Section7216 } from '@/features/account/section-7216/Section7216'
import {
  Section7216Acceptance,
  type Section7216AcceptanceProps,
} from '@/features/account/section-7216/Section7216Acceptance'
import { safeParseDate } from '@/utils/date-util'

export class Section7216Store {
  private readonly _accepted$ = new Subject<Section7216Acceptance>()
  readonly accepted$ = this._accepted$.asObservable()

  readonly section7216s = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new Section7216(),
  })

  readonly acceptances = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new Section7216Acceptance(),
  })

  constructor(private readonly _api: Section7216Api) {}

  async fetchLatest(signal?: AbortSignal): Promise<Section7216> {
    const res = await this._api.getLatest(signal)
    return this.section7216s.set(mapSection7216ResourceToProps(res))
  }

  async fetchById(id: Id, signal?: AbortSignal): Promise<Section7216> {
    const res = await this._api.getById(Id.assert(id), signal)
    return this.section7216s.set(mapSection7216ResourceToProps(res))
  }

  async fetchLatestAcceptance(
    signal?: AbortSignal,
  ): Promise<Section7216Acceptance | null> {
    try {
      const res = await this._api.getLatestAcceptance(signal)
      return this.acceptances.set(mapSection7216AcceptanceResourceToProps(res))
    } catch {
      return null
    }
  }

  async acceptLatest(options: {
    userSignature: string
    spouseSignature?: string
    signal?: AbortSignal
  }): Promise<Section7216Acceptance> {
    const latest = await this.fetchLatest(options.signal)

    const res = await this._api.accept({
      section_7216_id: Id.assert(latest.id),
      user_signature: options.userSignature,
      spouse_signature: options.spouseSignature,
    })

    const acceptance = this.acceptances.set(
      mapSection7216AcceptanceResourceToProps(res),
    )
    this._accepted$.next(acceptance)
    return acceptance
  }

  async listAcceptances(
    paging: CursorPageQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Section7216Acceptance>> {
    const response = await this._api.listAcceptances(
      { limit: paging.limit, after: paging.after },
      signal,
    )

    const items = this.acceptances.set(
      response.data.map(mapSection7216AcceptanceResourceToProps),
    )

    return CursorPaged.of(items, response.cursor)
  }
}

function mapSection7216ResourceToProps(resource: Section7216Resource) {
  return {
    id: resource.id,
    markup: resource.markup,
    dateCreated: safeParseDate(resource.date_created),
    dateModified: safeParseDate(resource.date_modified),
  }
}

function mapSection7216AcceptanceResourceToProps(
  resource: Section7216AcceptanceResource,
): Section7216AcceptanceProps {
  return {
    id: resource.id,
    section7216Id: resource.section_7216_id,
    userPublicId: resource.user_public_id,
    userSignature: resource.user_signature,
    spouseSignature: resource.spouse_signature,
    createdAt: safeParseDate(resource.date_created),
  }
}
